import { ChatDtoType } from '@server/generated/openapi/message';
import { z } from 'zod';
import association from '../common/association.dto';
import { pageFilter } from '../common/filters/common';
import reference, { Reference } from '../common/reference.dto';
import id from '../common/uuid.dto';
import { personCardOrReference } from '../person/person.dto';

const chatType = z.enum(ChatDtoType);

/** What a timeline row represents. */
export enum ChatTimelineType {
  MESSAGE = 'message',
  CREATE = 'create',
  ADD = 'add',
  REMOVE = 'remove',
  LEFT = 'left',
}

export enum ChatMessageDirection {
  SENT = 'sent',
  RECEIVED = 'received',
}

// Needed since we have "id: `${person.id}:${at.getTime()}`" in chat/info-message.mapping.ts
const syntheticTimelineEntryId = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}:\d+$/i,
  );

const chat = z.object({
  id,
  organization: reference,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  latestMessageAt: z.coerce.date().optional(),
  title: z.string(),
  unread: z.boolean(),
  numOfUnread: z.number(),
  pinnedAt: z.coerce.date().optional(),
  failed: z.boolean().optional(),
  type: chatType.optional(),
  groupChatOrganization: reference.optional(), // the selected organization for group chat
});

export const chatDetails = z.object({
  ...chat.shape,
  startedBy: personCardOrReference.optional(),
});
export const updateChat = z.object({
  title: z.string(),
});

export const CHAT_MESSAGE_MAX_LENGTH = 4000;

export const sendChat = z.object({
  text: z.string(),
});

export const getChatMessages = z.object({
  id,
  ...pageFilter.shape,
});

export const getChatMessagesByAccessCode = z.object({
  accessCode: z.string(),
  ...pageFilter.shape,
});

export const postChat = z.object({
  title: z.string().optional(),
  text: z.string(),
  includedPeople: reference.array().min(1),
  type: chatType,
  groupChatOrganization: reference.optional(),
});

export const includedPersonEvent = z.object({
  at: z.coerce.date(),
  by: personCardOrReference,
});

export const includedPerson = z.object({
  ...association.shape,
  person: personCardOrReference,
  created: includedPersonEvent.array(),
  removed: includedPersonEvent.array(),
});

export const chatTimelineEntry = z.object({
  id: z.union([id, syntheticTimelineEntryId]),
  chat: reference,
  type: z.enum(ChatTimelineType),
  createdAt: z.coerce.date(),
  by: personCardOrReference.optional(),
  /** MESSAGE only. */
  text: z.string().optional(),
  /** MESSAGE only. */
  direction: z.enum(ChatMessageDirection).optional(),
  /**
   * Who an ADD, REMOVE or LEFT row is about, newest first. Holds more than one
   * person when the events happened at the same time — see
   * chat/timeline.grouping.ts.
   */
  people: personCardOrReference.array().optional(),
  failed: z.boolean().optional(),
  unread: z.boolean().optional(),
});

export default chat;

export type Chat = z.infer<typeof chat>;
export type ChatDetails = z.infer<typeof chatDetails>;
export type PartialChat = Partial<Chat> & Reference;
export type ChatTimelineEntry = z.infer<typeof chatTimelineEntry>;
export type ChatIncludedPerson = z.infer<typeof includedPerson>;
export type ChatIncludedPersonEvent = z.infer<typeof includedPersonEvent>;
export type PostChat = z.infer<typeof postChat>;
export type SendChat = z.infer<typeof sendChat>;
export type UpdateChat = z.infer<typeof updateChat>;
export type GetChatMessages = z.infer<typeof getChatMessages>;
export type GetChatMessagesByAccessCode = z.infer<
  typeof getChatMessagesByAccessCode
>;
export const ChatType = chatType.enum;
export type ChatType = z.infer<typeof chatType>;

export const chatByAccessCode = z.object({
  title: z.string(),
  type: chatType.optional(),
  locale: z.string(),
  hasLeft: z.boolean().optional(),
});

export type ChatByAccessCode = z.infer<typeof chatByAccessCode>;
